import { CdkTrapFocus, CdkAriaLive } from '@angular/cdk/a11y';
import { CdkDrag, CdkDragHandle, CdkDropList, CdkDropListGroup } from '@angular/cdk/drag-drop';
import { LayoutModule } from '@angular/cdk/layout';
import { TextFieldModule } from '@angular/cdk/text-field';
import { CommonModule, NgOptimizedImage, PRECONNECT_CHECK_BLOCKLIST } from '@angular/common';
import { Injectable, NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_RIPPLE_GLOBAL_OPTIONS } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HAMMER_GESTURE_CONFIG, HammerGestureConfig, HammerModule } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { Diagnostic } from '@awesome-cordova-plugins/diagnostic/ngx';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { IonicModule } from '@ionic/angular';

import { TranslateModule } from '@ngx-translate/core';
import * as Hammer from 'hammerjs';
import { SwiperModule } from 'swiper/angular';

import { EiqFilesModule } from '@eiq/files';
import { ToPlainTextPipe } from '@eiq/shared/utils';

import { AudioModule } from '@empathy/audio';
import { SurveyCoreModule } from '@empathy/survey-core';

import { EmpathyViewerButtonComponent } from './components/button/button.component';
import { EmpathyCardComponent } from './components/card/card.component';
import { EndPageViewComponent } from './components/end-page-view/end-page-view.component';
import { ErrorContainerComponent } from './components/error-container/error-container.component';
import { FeedbackViewComponent } from './components/feedback-view/feedback-view.component';
import { EmpathyViewerIconButtonComponent } from './components/icon-button/icon-button.component';
import { MediaViewComponent } from './components/media-view/media-view.component';
import { PageViewComponent } from './components/page-view/page-view.component';
import { EmpathyViewerProgressBarComponent } from './components/progress-bar/progress-bar.component';
import { AIChatQuestionViewComponent } from './components/question-view/ai-chat-question-view/ai-chat-question-view.component';
import { KeyboardAudioComponent } from './components/question-view/ai-chat-question-view/keyboard-audio/keyboard-audio.component';
import { LocalAudioPlayerComponent } from './components/question-view/ai-chat-question-view/local-audio-player/local-audio-player.component';
import { CarouselMultiChoiceQuestionViewComponent } from './components/question-view/carousel-multi-choice-question-view/carousel-multi-choice-question-view.component';
import { CarouselQuestionViewComponent } from './components/question-view/carousel-question-view/carousel-question-view.component';
import { CarouselRatingQuestionViewComponent } from './components/question-view/carousel-rating-question-view/carousel-rating-question-view.component';
import { DropdownQuestionViewComponent } from './components/question-view/dropdown-question-view/dropdown-question-view.component';
import { EssayQuestionViewComponent } from './components/question-view/essay-question-view/essay-question-view.component';
import { FileUploadQuestionViewComponent } from './components/question-view/file-upload-question-view/file-upload-question-view.component';
import { HeatmapQuestionViewComponent } from './components/question-view/heatmap-question-view/heatmap-question-view.component';
import { InfoPageQuestionViewComponent } from './components/question-view/info-page-question-view/info-page-question-view.component';
import { InputQuestionViewComponent } from './components/question-view/input-question-view/input-question-view.component';
import { MultiChoiceQuestionViewComponent } from './components/question-view/multi-choice-question-view/multi-choice-question-view.component';
import { NPSQuestionViewComponent } from './components/question-view/nps-question-view/nps-question-view.component';
import { OptionMediaComponent } from './components/question-view/option-media/option-media.component';
import { QuestionViewComponent } from './components/question-view/question-view.component';
import { RankSortQuestionViewComponent } from './components/question-view/rank-sort-question-view/rank-sort-question-view.component';
import { RankingQuestionViewComponent } from './components/question-view/ranking-question-view/ranking-question-view.component';
import { RatingQuestionViewComponent } from './components/question-view/rating-question-view/rating-question-view.component';
import { SingleChoiceQuestionViewComponent } from './components/question-view/single-choice-question-view/single-choice-question-view.component';
import { StimulusComponent } from './components/stimulus/stimulus.component';
import { EmpathyTemplateCardComponent } from './components/template-card/template-card.component';
import { WelcomePageViewComponent } from './components/welcome-page-view/welcome-page-view.component';
import { ViewerComponent } from './containers/viewer/viewer.component';
import { RatingsPositionDirective } from './directives/ratings-position.directive';
import { ScrollToSelectedDirective } from './directives/scroll-to-message.directive';
import { CharactersLeftPipe } from './pipes/characters-left.pipe';
import { CheckedPipe } from './pipes/checked.pipe';
import { ProgressPipe } from './pipes/progress.pipe';

@Injectable()
export class HammerConfig extends HammerGestureConfig {
  overrides = {
    swipe: { direction: Hammer.DIRECTION_ALL }
  };
}

@NgModule({
  imports: [
    AudioModule,
    CommonModule,
    EiqFilesModule,
    FontAwesomeModule,
    HammerModule,
    IonicModule,
    LayoutModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatCheckboxModule,
    MatInputModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatSelectModule,
    MatSnackBarModule,
    MatToolbarModule,
    MatTooltipModule,
    NgOptimizedImage,
    ReactiveFormsModule,
    RouterModule,
    SurveyCoreModule,
    SwiperModule,
    TextFieldModule,
    ToPlainTextPipe,
    TranslateModule,
    CdkDropList,
    CdkTrapFocus,
    CdkDragHandle,
    CdkDrag,
    CdkDropListGroup,
    CdkAriaLive
  ],
  declarations: [
    AIChatQuestionViewComponent,
    CarouselMultiChoiceQuestionViewComponent,
    CarouselQuestionViewComponent,
    CarouselRatingQuestionViewComponent,
    EndPageViewComponent,
    EmpathyCardComponent,
    EmpathyTemplateCardComponent,
    EmpathyViewerButtonComponent,
    EmpathyViewerIconButtonComponent,
    EmpathyViewerProgressBarComponent,
    DropdownQuestionViewComponent,
    ErrorContainerComponent,
    EssayQuestionViewComponent,
    FeedbackViewComponent,
    FileUploadQuestionViewComponent,
    HeatmapQuestionViewComponent,
    InfoPageQuestionViewComponent,
    InputQuestionViewComponent,
    KeyboardAudioComponent,
    LocalAudioPlayerComponent,
    MediaViewComponent,
    MultiChoiceQuestionViewComponent,
    NPSQuestionViewComponent,
    OptionMediaComponent,
    PageViewComponent,
    QuestionViewComponent,
    RankSortQuestionViewComponent,
    RankingQuestionViewComponent,
    RatingQuestionViewComponent,
    SingleChoiceQuestionViewComponent,
    StimulusComponent,
    ViewerComponent,
    WelcomePageViewComponent,
    RatingsPositionDirective,
    ScrollToSelectedDirective,
    CharactersLeftPipe,
    CheckedPipe,
    ProgressPipe
  ],
  providers: [
    Diagnostic,
    { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: { disabled: true } },
    { provide: HAMMER_GESTURE_CONFIG, useClass: HammerConfig },
    { provide: PRECONNECT_CHECK_BLOCKLIST, useValue: 'https://mompublicimages.blob.core.windows.net' }
  ],
  exports: [EndPageViewComponent, FeedbackViewComponent, PageViewComponent, QuestionViewComponent, ViewerComponent, WelcomePageViewComponent]
})
export class EmpathyViewerModule {}
